// Versioned client/server wire protocol.
//
// Every message is a small, flat JSON object tagged by `t`. encode() is a
// thin JSON.stringify wrapper; decodeClient/decodeServer are the trust
// boundary for anything that arrived over the network and MUST NEVER THROW,
// no matter what bytes show up. They return null on any parse or shape
// failure, and for `input` they clamp out-of-range values rather than
// rejecting the whole message (a slightly-too-large throttle value from a
// buggy or malicious client shouldn't drop the player's whole input frame).
//
// decodeServer performs the same never-throw, shape-validating decode for
// messages travelling the other direction. The server is (mostly) trusted
// code we wrote ourselves, so decodeServer validates shape but does not
// clamp/normalise values the way decodeClient does for untrusted client
// input.

import type { CarState, RacePhase, Scores } from '../sim/types';
import type { RaceConfig, CarSlot, RaceState } from '../sim/race';

export const PROTOCOL_VERSION = 1;

export type Difficulty = 'easy' | 'normal' | 'hard';

export interface LobbyPlayer {
  id: number;
  name: string;
  colour: number;
  ready: boolean;
  connected: boolean;
  isHost: boolean;
}

export interface LobbyState {
  code: string;
  players: LobbyPlayer[];
  aiCount: number;
  difficulty: Difficulty;
  trackId: string;
  phase: 'lobby' | 'racing' | 'results';
}

export interface CarSnap {
  id: number;
  x: number;
  y: number;
  h: number;
  vx: number;
  vy: number;
  air: number;
  lap: number;
  lp: number;
  el: 0 | 1;
  // Derived flag (wrongWayTime > 0.5s) for cheap display/reaction on the
  // client. bd/wwt below carry the raw accumulators so a client running its
  // own prediction can continue the wrong-way detection math itself instead
  // of just mirroring this flag.
  ww: 0 | 1;
  bd: number; // CarState.backDist: accumulated backward centreline travel
  wwt: number; // CarState.wrongWayTime: seconds spent going the wrong way
}

export interface Snapshot {
  tick: number;
  phase: RacePhase;
  phaseTime: number;
  cars: CarSnap[];
  cam: { x: number; y: number };
  scores: Scores;
  leader: number;
  ack: number;
  roundWinner: number | null;
  ranking: number[];
  // From RaceState.countdownLength, so clients can render the countdown
  // (the initial grid countdown is longer than the post-round one).
  countdownLength: number;
}

export type ClientMessage =
  | { t: 'create'; v: number; name: string; session?: string }
  | { t: 'join'; v: number; code: string; name: string; session?: string }
  | { t: 'setColour'; colour: number }
  | { t: 'ready'; ready: boolean }
  | { t: 'setAi'; count: number; difficulty: Difficulty }
  | { t: 'start' }
  | { t: 'input'; seq: number; th: number; br: number; st: number }
  | { t: 'rematch' }
  | { t: 'leave' }
  | { t: 'ping'; c: number }
  // Dev-only override of race config fields. The server only honours this
  // outside production; the shape itself is unconditional.
  | { t: 'devConfig'; cfg: Partial<RaceConfig> };

export type ServerMessage =
  | { t: 'welcome'; playerId: number; session: string; lobby: LobbyState }
  | { t: 'lobby'; lobby: LobbyState }
  | { t: 'raceStart'; trackId: string; slots: CarSlot[]; seed: number; cfg: RaceConfig; yourCarId: number }
  | { t: 'snap'; s: Snapshot }
  | { t: 'results'; ranking: number[]; scores: Scores; slots: CarSlot[] }
  // No 'nameTaken' code: Room.join dedupes a clashing name (Martin, Martin2,
  // ...) rather than rejecting the join, so nothing ever sends one.
  | { t: 'error'; code: 'badCode' | 'full' | 'notHost' | 'version'; msg: string }
  | { t: 'pong'; c: number; serverTick: number };

export function encode(m: ClientMessage | ServerMessage): string {
  return JSON.stringify(m);
}

// ---------------------------------------------------------------------------
// Shared low-level helpers
// ---------------------------------------------------------------------------

type Rec = Record<string, unknown>;

function isRecord(x: unknown): x is Rec {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

function isNum(x: unknown): x is number {
  return typeof x === 'number' && Number.isFinite(x);
}

function numOr(x: unknown, fallback: number): number {
  return isNum(x) ? x : fallback;
}

function clamp(x: unknown, lo: number, hi: number): number {
  const n = numOr(x, 0);
  return Math.min(hi, Math.max(lo, n));
}

// Clamps to a non-negative integer; NaN/non-numeric becomes 0.
function clampSeq(x: unknown): number {
  const n = numOr(x, 0);
  const floored = Math.floor(n);
  return floored < 0 ? 0 : floored;
}

// Trims to 1..16 characters, defaulting to "Driver" when empty/blank/absent.
function normalizeName(x: unknown): string {
  const raw = typeof x === 'string' ? x.trim() : '';
  const name = raw.length === 0 ? 'Driver' : raw;
  return name.length > 16 ? name.slice(0, 16) : name;
}

function normalizeCode(x: unknown): string {
  return typeof x === 'string' ? x.trim().toUpperCase() : '';
}

function isDifficulty(x: unknown): x is Difficulty {
  return x === 'easy' || x === 'normal' || x === 'hard';
}

const RACE_CONFIG_KEYS: (keyof RaceConfig)[] = [
  'pointCap',
  'lapLimit',
  'viewW',
  'viewH',
  'offscreenGrace',
  'roundEndPause',
  'countdown',
];

function isRaceConfig(x: unknown): x is RaceConfig {
  if (!isRecord(x)) return false;
  return RACE_CONFIG_KEYS.every((k) => isNum(x[k]));
}

function partialRaceConfig(x: Rec): Partial<RaceConfig> {
  const out: Partial<RaceConfig> = {};
  for (const k of RACE_CONFIG_KEYS) {
    const v = x[k];
    if (isNum(v)) (out as Record<string, number>)[k] = v;
  }
  return out;
}

function isCarSlot(x: unknown): x is CarSlot {
  if (!isRecord(x)) return false;
  if (!isNum(x.id)) return false;
  if (typeof x.name !== 'string') return false;
  if (!isNum(x.colour)) return false;
  if (x.kind !== 'human' && x.kind !== 'ai') return false;
  if (x.difficulty !== undefined && !isDifficulty(x.difficulty)) return false;
  return true;
}

function isCarSlotArray(x: unknown): x is CarSlot[] {
  return Array.isArray(x) && x.every(isCarSlot);
}

function isLobbyPlayer(x: unknown): x is LobbyPlayer {
  if (!isRecord(x)) return false;
  return (
    isNum(x.id) &&
    typeof x.name === 'string' &&
    isNum(x.colour) &&
    typeof x.ready === 'boolean' &&
    typeof x.connected === 'boolean' &&
    typeof x.isHost === 'boolean'
  );
}

function isLobbyState(x: unknown): x is LobbyState {
  if (!isRecord(x)) return false;
  if (typeof x.code !== 'string') return false;
  if (!Array.isArray(x.players) || !x.players.every(isLobbyPlayer)) return false;
  if (!isNum(x.aiCount)) return false;
  if (!isDifficulty(x.difficulty)) return false;
  if (typeof x.trackId !== 'string') return false;
  if (x.phase !== 'lobby' && x.phase !== 'racing' && x.phase !== 'results') return false;
  return true;
}

function isRacePhase(x: unknown): x is RacePhase {
  return x === 'countdown' || x === 'racing' || x === 'roundEnd' || x === 'finished';
}

function isCarSnap(x: unknown): x is CarSnap {
  if (!isRecord(x)) return false;
  return (
    isNum(x.id) &&
    isNum(x.x) &&
    isNum(x.y) &&
    isNum(x.h) &&
    isNum(x.vx) &&
    isNum(x.vy) &&
    isNum(x.air) &&
    isNum(x.lap) &&
    isNum(x.lp) &&
    (x.el === 0 || x.el === 1) &&
    (x.ww === 0 || x.ww === 1) &&
    isNum(x.bd) &&
    isNum(x.wwt)
  );
}

function isScores(x: unknown): x is Scores {
  if (!isRecord(x)) return false;
  return Object.values(x).every((v) => isNum(v));
}

function isSnapshot(x: unknown): x is Snapshot {
  if (!isRecord(x)) return false;
  if (!isNum(x.tick)) return false;
  if (!isRacePhase(x.phase)) return false;
  if (!isNum(x.phaseTime)) return false;
  if (!Array.isArray(x.cars) || !x.cars.every(isCarSnap)) return false;
  if (!isRecord(x.cam) || !isNum(x.cam.x) || !isNum(x.cam.y)) return false;
  if (!isScores(x.scores)) return false;
  if (!isNum(x.leader)) return false;
  if (!isNum(x.ack)) return false;
  if (x.roundWinner !== null && !isNum(x.roundWinner)) return false;
  if (!Array.isArray(x.ranking) || !x.ranking.every((n) => isNum(n))) return false;
  if (!isNum(x.countdownLength)) return false;
  return true;
}

const ERROR_CODES = ['badCode', 'full', 'notHost', 'version'] as const;

// ---------------------------------------------------------------------------
// decodeClient
// ---------------------------------------------------------------------------

export function decodeClient(s: string): ClientMessage | null {
  let raw: unknown;
  try {
    raw = JSON.parse(s);
  } catch {
    return null;
  }
  if (!isRecord(raw) || typeof raw.t !== 'string') return null;

  switch (raw.t) {
    case 'create': {
      const msg: ClientMessage = { t: 'create', v: numOr(raw.v, 0), name: normalizeName(raw.name) };
      if (typeof raw.session === 'string') (msg as { session?: string }).session = raw.session;
      return msg;
    }
    case 'join': {
      const msg: ClientMessage = {
        t: 'join',
        v: numOr(raw.v, 0),
        code: normalizeCode(raw.code),
        name: normalizeName(raw.name),
      };
      if (typeof raw.session === 'string') (msg as { session?: string }).session = raw.session;
      return msg;
    }
    case 'setColour':
      return { t: 'setColour', colour: numOr(raw.colour, 0) };
    case 'ready':
      return { t: 'ready', ready: Boolean(raw.ready) };
    case 'setAi':
      return {
        t: 'setAi',
        count: numOr(raw.count, 0),
        difficulty: isDifficulty(raw.difficulty) ? raw.difficulty : 'normal',
      };
    case 'start':
      return { t: 'start' };
    case 'input':
      return {
        t: 'input',
        seq: clampSeq(raw.seq),
        th: clamp(raw.th, 0, 1),
        br: clamp(raw.br, 0, 1),
        st: clamp(raw.st, -1, 1),
      };
    case 'rematch':
      return { t: 'rematch' };
    case 'leave':
      return { t: 'leave' };
    case 'ping':
      return { t: 'ping', c: numOr(raw.c, 0) };
    case 'devConfig': {
      if (!isRecord(raw.cfg)) return null;
      return { t: 'devConfig', cfg: partialRaceConfig(raw.cfg) };
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// decodeServer
// ---------------------------------------------------------------------------

export function decodeServer(s: string): ServerMessage | null {
  let raw: unknown;
  try {
    raw = JSON.parse(s);
  } catch {
    return null;
  }
  if (!isRecord(raw) || typeof raw.t !== 'string') return null;

  switch (raw.t) {
    case 'welcome': {
      if (!isNum(raw.playerId) || typeof raw.session !== 'string' || !isLobbyState(raw.lobby)) return null;
      return { t: 'welcome', playerId: raw.playerId, session: raw.session, lobby: raw.lobby };
    }
    case 'lobby': {
      if (!isLobbyState(raw.lobby)) return null;
      return { t: 'lobby', lobby: raw.lobby };
    }
    case 'raceStart': {
      if (
        typeof raw.trackId !== 'string' ||
        !isCarSlotArray(raw.slots) ||
        !isNum(raw.seed) ||
        !isRaceConfig(raw.cfg) ||
        !isNum(raw.yourCarId)
      ) {
        return null;
      }
      return { t: 'raceStart', trackId: raw.trackId, slots: raw.slots, seed: raw.seed, cfg: raw.cfg, yourCarId: raw.yourCarId };
    }
    case 'snap': {
      if (!isSnapshot(raw.s)) return null;
      return { t: 'snap', s: raw.s };
    }
    case 'results': {
      if (
        !Array.isArray(raw.ranking) ||
        !raw.ranking.every((n) => isNum(n)) ||
        !isScores(raw.scores) ||
        !isCarSlotArray(raw.slots)
      ) {
        return null;
      }
      return { t: 'results', ranking: raw.ranking, scores: raw.scores, slots: raw.slots };
    }
    case 'error': {
      if (typeof raw.msg !== 'string' || !ERROR_CODES.includes(raw.code as (typeof ERROR_CODES)[number])) return null;
      return { t: 'error', code: raw.code as (typeof ERROR_CODES)[number], msg: raw.msg };
    }
    case 'pong': {
      if (!isNum(raw.c) || !isNum(raw.serverTick)) return null;
      return { t: 'pong', c: raw.c, serverTick: raw.serverTick };
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Snapshot <-> RaceState / CarState
// ---------------------------------------------------------------------------

function carToSnap(car: CarState): CarSnap {
  return {
    id: car.id,
    x: car.pos.x,
    y: car.pos.y,
    h: car.heading,
    vx: car.vel.x,
    vy: car.vel.y,
    air: car.airborne,
    lap: car.lap,
    lp: car.lapProgress,
    el: car.eliminated ? 1 : 0,
    ww: car.wrongWayTime > 0.5 ? 1 : 0,
    bd: car.backDist,
    wwt: car.wrongWayTime,
  };
}

// ack is per-recipient (each connection acks the highest input seq it has
// applied), so the caller patches it onto the shared snapshot per player
// rather than this function knowing about players/connections.
export function toSnapshot(state: RaceState, ack: number): Snapshot {
  return {
    tick: state.tick,
    phase: state.phase,
    phaseTime: state.phaseTime,
    cars: state.cars.map(carToSnap),
    cam: { x: state.camera.pos.x, y: state.camera.pos.y },
    scores: state.scores,
    leader: state.leaderId,
    ack,
    roundWinner: state.roundWinnerId,
    ranking: state.ranking,
    countdownLength: state.countdownLength,
  };
}

// Restores pos, heading, vel, airborne, lap, lapProgress, eliminated,
// backDist and wrongWayTime from a CarSnap (the latter two so a client
// running its own prediction can keep evolving the wrong-way accumulators
// after reconciling to a snapshot, rather than only knowing the derived
// `ww` flag). `progress` (metres along the centreline, monotonic across
// laps) is only recomputed when a trackLength is supplied, as
// `lap * trackLength + lapProgress`; without it, `car.progress` is left
// untouched, since CarSnap alone doesn't carry enough information to
// reconstruct it (the caller may not always have the track at hand, e.g.
// while reconciling a car it isn't currently simulating against).
//
// `car.surface` is deliberately NOT restored and is NOT on the wire: it is
// recomputed from the car's position on the very next stepCarOnTrack call
// (see sim/step.ts, which assigns car.surface every step), so carrying a
// per-car surface name in every 20Hz snapshot would be pure wire bloat for
// a value that is overwritten before anything reads it. Nothing outside
// stepCarOnTrack consumes car.surface; callers that need the surface under
// an arbitrary point call surfaceAt(track, pos) directly.
export function applySnapCar(car: CarState, s: CarSnap, trackLength?: number): void {
  car.pos = { x: s.x, y: s.y };
  car.heading = s.h;
  car.vel = { x: s.vx, y: s.vy };
  car.airborne = s.air;
  car.lap = s.lap;
  car.lapProgress = s.lp;
  car.eliminated = s.el === 1;
  car.backDist = s.bd;
  car.wrongWayTime = s.wwt;
  if (trackLength !== undefined) {
    car.progress = car.lap * trackLength + car.lapProgress;
  }
}
